import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { downloadSpreadsheet } from "../../utils/excelSheet";
import "../../CSS/Global.css";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { decryptToken } from "../utils/getToken";

const formatDate = (d) => {
    if (!d) return "";
    try {
        return new Date(d).toISOString().split("T")[0];
    } catch {
        return String(d);
    }
};

const ExportAdmission = () => {
    const navigate = useNavigate();
    const [isSpecificDateSelected, setIsSpecificDateSelected] = useState(false);
    const [fileFormat, setFileFormat] = useState("xlsx");
    const [admissionRows, setAdmissionRows] = useState([]);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const url = API_BASE_URL;
    const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
    const token = decryptToken("accessToken") || "";

    useEffect(() => {
        if (!isLoggedIn?.main_id) return;

        const headers = {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };

        fetch(`${url}/admission/alladmin/${isLoggedIn.main_id}`, { headers })
            .then((res) => res.json())
            .then((data) => {
                const rows = Array.isArray(data.user) ? data.user : [];
                setAdmissionRows(rows);
            })
            .catch((err) => console.error("Failed to fetch admissions:", err));
    }, [isLoggedIn?.main_id, url, token]);

    const handleRadioChange = (e) => {
        setIsSpecificDateSelected(e.target.value === "specificDate");
    };

    const handleExport = async () => {
        if (!fileFormat) return alert("Please select a file format.");
        if (admissionRows.length === 0) return alert("No admission data found.");

        let filteredData = [...admissionRows];

        if (isSpecificDateSelected) {
            if (!startDate || !endDate) return alert("Please select both start and end dates.");

            filteredData = admissionRows.filter((row) => {
                const date = new Date(row.createdAt);
                const start = new Date(startDate);
                const end = new Date(endDate);
                end.setHours(23, 59, 59, 999);
                return date >= start && date <= end;
            });

            if (filteredData.length === 0) {
                return alert("No admissions found in the selected date range.");
            }
        }

        const cleanedData = filteredData.map((r) => ({
            "Admission ID": r.admission_id || "",
            "Payment ID": r.payment_id || "",
            "Name": r.name || "",
            "Father Name": r.fatherName || "",
            "Mother Name": r.motherName || "",
            "Date of Birth": formatDate(r.dob),
            "Aadhar": r.aadhar || "",
            "Gender": r.gender || "",
            "School": r.school || "",
            "Address": r.address || "",
            "Pincode": r.pincode || "",
            "Mobile": r.mobile || "",
            "Email": r.email || "",
            "Center": r.center || "",
            "Status": r.status || "",
            "Rules Agreed": r.rulesAgreed || "",
            "Created At": formatDate(r.createdAt),
        }));

        const baseName = `admissions_${Date.now()}`;
        const fmt = (fileFormat || "xlsx").toLowerCase();

        if (fmt === "csv") {
            await downloadSpreadsheet(cleanedData, "Admissions", baseName, "csv");
            return;
        }

        /* xls / xlsx: same OOXML workbook (.xlsx). Legacy BIFF .xls is not generated. */
        await downloadSpreadsheet(cleanedData, "Admissions", baseName, "xlsx");
    };

    return (
        <div>
            <div className="export-card">
                <div className="export-radio">
                    <div className="export-radio-list">
                        <input
                            type="radio"
                            name="exportOption"
                            value="allAdmissions"
                            checked={!isSpecificDateSelected}
                            onChange={handleRadioChange}
                        />
                        <label>All Admissions</label>
                    </div>
                    <div className="export-radio-list">
                        <input
                            type="radio"
                            name="exportOption"
                            value="specificDate"
                            checked={isSpecificDateSelected}
                            onChange={handleRadioChange}
                        />
                        <label>Specific Date</label>
                    </div>
                </div>

                {isSpecificDateSelected && (
                    <div className="date-range">
                        <div className="date-range-list">
                            <label>Start Date:</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </div>
                        <div className="date-range-list">
                            <label>End Date:</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </div>
                    </div>
                )}

                <div className="export-file-type">
                    <p className="export-file-header">Select file format:</p>
                    <div className="export-file-options">
                        {["csv", "xls", "xlsx"].map((fmt) => (
                            <div className="export-file-option-list" key={fmt}>
                                <input
                                    type="radio"
                                    name="fileFormat"
                                    id={`adm-${fmt}`}
                                    value={fmt}
                                    checked={fileFormat === fmt}
                                    onChange={(e) => setFileFormat(e.target.value)}
                                />
                                <label htmlFor={`adm-${fmt}`}>{fmt.toUpperCase()}</label>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="export-buttons">
                    <button className="export-button" type="button" onClick={handleExport}>
                        Export
                    </button>
                    <button
                        className="export-cancel-button"
                        type="button"
                        onClick={() => navigate(-1)}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExportAdmission;
